import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Card, ConfirmDialog, StatusBadge, useCart, useToast } from '@kss/ui';
import { useAuth } from '@kss/auth';
import { authService, salesOrderService, type SalesOrder } from '@kss/api';
import { ChevronRight, CheckCircle2, Clock, Download, Package, RefreshCw, Truck, XCircle } from 'lucide-react';
import { reorderFromOrder } from './reorderFromOrder';

/**
 * Customer-facing order history.
 *
 * Pulls orders from `salesOrderService.filter({ customerId })`. The order
 * lifecycle (pending → confirmed → packed → shipped → delivered) is rendered
 * as a vertical timeline in the expandable detail panel.
 *
 * Supported actions per order:
 * - **Cancel** — issues `salesOrderService.update` with `status: 5`
 *   (SalesOrderStatus.Cancelled — the int enum, not the label, because
 *   the live PUT silently no-ops on the string form).
 *   Only available before the order is shipped.
 * - **Reorder** — adds every item from the order back into the cart drawer.
 *   (When the line-item service ships, this will pull lines via that
 *   endpoint; for now the user reopens the cart with whatever the order
 *   captured at checkout.)
 * - **Download invoice** — opens a print-friendly receipt in a new window.
 *
 * Until the backend exposes a customer-scoped feed, the seed orders below
 * are merged in for empty tenants so the UI stays demo-able.
 */

type Stage =
  | 'pending'
  | 'confirmed'
  | 'packed'
  | 'shipped'
  | 'out_for_delivery'
  | 'delivered'
  | 'cancelled'
  | 'returned';

interface UiHistory {
  stage: Stage;
  at: string;
  note?: string;
}

interface UiOrder {
  id: string;
  raw: SalesOrder;
  date: string;
  total: number;
  itemsCount: number;
  status: Stage;
  history: UiHistory[];
}

const STAGE_LABEL: Record<Stage, string> = {
  pending: 'Order placed',
  confirmed: 'Confirmed',
  packed: 'Packed',
  shipped: 'Shipped',
  out_for_delivery: 'Out for delivery',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
  returned: 'Returned',
};

const STAGE_ICON: Record<Stage, typeof Clock> = {
  pending: Clock,
  confirmed: CheckCircle2,
  packed: Package,
  shipped: Truck,
  out_for_delivery: Truck,
  delivered: CheckCircle2,
  cancelled: XCircle,
  returned: XCircle,
};

function statusToken(s: Stage): string {
  if (s === 'delivered') return 'completed';
  if (s === 'cancelled' || s === 'returned') return 'cancelled';
  if (s === 'pending' || s === 'confirmed') return 'pending';
  return 'active';
}

function rawToStage(raw?: number | string | null): Stage {
  // Live API returns `status` as int enum; legacy mocks used strings.
  // Map both to the UI stage tokens.
  //
  // SSO NEW ISSUE 3 — SalesOrderStatus = 6 is the code-owned "Returned"
  // extension that backend `OrderReturnService` writes when an admin
  // creates a return for this order. Recognising it here is what lets
  // the split below route the row into "Returns & Refunds" instead of
  // leaving it under active "My orders".
  if (typeof raw === 'number') {
    if (raw === 6) return 'returned';
    if (raw === 5) return 'cancelled';
    if (raw === 4) return 'delivered';
    if (raw === 3) return 'shipped';
    if (raw === 2) return 'confirmed';
    return 'pending';
  }
  const v = String(raw ?? '').toLowerCase();
  if (v === 'cancelled' || v === 'canceled') return 'cancelled';
  if (v === 'delivered') return 'delivered';
  if (v === 'shipped') return 'shipped';
  if (v === 'confirmed') return 'confirmed';
  if (v === 'returned') return 'returned';
  return 'pending';
}

function isCancellable(stage: Stage): boolean {
  return stage === 'pending' || stage === 'confirmed';
}

function adapt(orders: SalesOrder[]): UiOrder[] {
  return orders
    .filter((o) => !!o.salesOrderId)
    .map((o) => {
      const stage = rawToStage(o.status);
      const at = o.orderDate ?? o.createdAt ?? '';
      const baseline: UiHistory[] = [
        { stage: 'pending', at },
        ...(stage !== 'pending' ? [{ stage, at: o.updatedAt ?? at }] : []),
      ];
      return {
        id: String(o.salesOrderId ?? o.orderNumber ?? ''),
        raw: o,
        date: (o.orderDate ?? o.createdAt ?? '').slice(0, 10),
        total: Number(o.totalAmount ?? 0),
        itemsCount: 0,
        status: stage,
        history: baseline,
      };
    });
}

export default function AccountOrders() {
  const { user } = useAuth();
  const toast = useToast();
  const cart = useCart();
  const navigate = useNavigate();

  const [orders, setOrders] = useState<UiOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState<UiOrder | null>(null);
  const [acting, setActing] = useState(false);

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      // Customer-scoped feed — `/api/auth/me/orders` filters strictly
      // to the JWT `customerId`, so tenant-scope leakage isn't
      // possible. Verified live 2026-05-05.
      const resp = await authService.myOrders({ pageSize: 100 });
      const items = Array.isArray(resp?.items) ? (resp.items as SalesOrder[]) : [];
      setOrders(adapt(items));
    } catch (e) {
      setError((e as Error)?.message ?? 'Failed to load orders');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const sorted = useMemo(
    () => [...orders].sort((a, b) => (b.date > a.date ? 1 : -1)),
    [orders]
  );

  /*
   * SSO NEW ISSUE 3 — split the customer's history into active vs returned
   * buckets so an order that admin marked Returned no longer shows up under
   * "My orders". Returns move into the "Returns & Refunds" section below.
   * Cancelled orders stay in the active list on purpose — the customer
   * cancelled them themselves and the cancel confirmation is the outcome.
   */
  const activeOrders = useMemo(() => sorted.filter((o) => o.status !== 'returned'), [sorted]);
  const returnedOrders = useMemo(() => sorted.filter((o) => o.status === 'returned'), [sorted]);

  async function doCancel() {
    if (!cancelling) return;
    setActing(true);
    try {
      /* Send the INT enum value (5 = Cancelled) directly, not the
         string label. Backend's `StringOrIntJsonConverter` is meant
         to accept both, but the live deploy returns `success: true`
         while leaving the status untouched when fed the string —
         the All-Orders grid then continues to show the prior status
         (e.g. "Delivered"). The int round-trips cleanly. Bug ref:
         AI Agent 2026-06-11 — "new order cancelled from frontend
         does not update status, still shows Delivered".
         SalesOrderStatus enum: 1=Draft 2=Confirmed 3=Shipped
         4=Delivered 5=Cancelled. */
      const next: SalesOrder = { ...cancelling.raw, status: 5 };
      await salesOrderService.update([next]);
      toast.success('Order cancelled', cancelling.raw.orderNumber ?? cancelling.id);
      setCancelling(null);
      await load();
    } catch (e) {
      toast.error('Cancel failed', (e as Error)?.message ?? 'Try again later.');
    } finally {
      setActing(false);
    }
  }

  async function doReorder(o: UiOrder) {
    // Re-add every line item from this order. Lookups go through the
    // public catalog so a returning shopper can reorder even if their
    // JWT has gone stale; products that have since been deleted are
    // skipped and reported to the user.
    setActing(true);
    try {
      const { added, skipped, total } = await reorderFromOrder(cart, o.id);
      cart.openDrawer();
      if (added === 0) {
        toast.error(
          'Could not re-add items',
          total === 0
            ? `No items recorded on ${o.raw.orderNumber ?? o.id}.`
            : 'Those products are no longer available.'
        );
      } else if (skipped > 0) {
        toast.info(
          'Cart updated',
          `Added ${added} of ${total} items from ${o.raw.orderNumber ?? o.id}. ${skipped} no longer available.`
        );
      } else {
        toast.success(
          'Cart updated',
          `Added ${added} ${added === 1 ? 'item' : 'items'} from ${o.raw.orderNumber ?? o.id}.`
        );
      }
    } catch (e) {
      toast.error('Reorder failed', (e as Error)?.message ?? 'Try again later.');
    } finally {
      setActing(false);
    }
  }

  function doDownloadInvoice(o: UiOrder) {
    const w = window.open('', '_blank');
    if (!w) {
      toast.error('Popup blocked', 'Allow popups to download the invoice.');
      return;
    }
    const html = `<!doctype html><html><head><title>Invoice ${o.raw.orderNumber ?? o.id}</title>
      <style>body{font:14px system-ui;padding:24px}h1{font-size:18px}table{border-collapse:collapse;width:100%}td,th{border:1px solid #ddd;padding:6px}</style>
      </head><body>
      <h1>Invoice ${o.raw.orderNumber ?? o.id}</h1>
      <p>Date: ${o.date}</p>
      <p>Status: ${STAGE_LABEL[o.status]}</p>
      <table><tr><th>Order #</th><td>${o.raw.orderNumber ?? '—'}</td></tr>
      <tr><th>Channel</th><td>${o.raw.channel ?? '—'}</td></tr>
      <tr><th>Subtotal</th><td>₹ ${Number(o.raw.totalAmount ?? 0).toLocaleString('en-IN')}</td></tr>
      <tr><th>Tax</th><td>₹ ${Number(o.raw.taxAmount ?? 0).toLocaleString('en-IN')}</td></tr>
      <tr><th>Shipping</th><td>₹ ${Number(o.raw.shippingAmount ?? 0).toLocaleString('en-IN')}</td></tr>
      <tr><th>Total</th><td><b>₹ ${Number(o.total).toLocaleString('en-IN')}</b></td></tr></table>
      <script>window.onload=()=>setTimeout(()=>window.print(),200)</script>
      </body></html>`;
    w.document.open();
    w.document.write(html);
    w.document.close();
  }

  /*
   * SSO NEW ISSUE 3 — the same order card renders in both the active and the
   * returned sections. Extracted as an inline renderer so the two sections
   * stay visually identical without a copy-paste that could drift.
   */
  const renderOrderCard = (o: UiOrder) => (
    <Card key={o.id} className="p-0">
      <div className="flex items-center justify-between gap-2 px-5 py-4">
        <button
          type="button"
          onClick={() => navigate(`/account/orders/${o.id}`)}
          className="grid flex-1 gap-0.5 text-left hover:opacity-80"
          title="Open order details"
        >
          <p className="text-sm font-semibold text-[var(--color-text-primary)]">
            {o.raw.orderNumber ?? o.id}
          </p>
          <p className="text-xs text-[var(--color-text-secondary)]">
            Placed {o.date || '—'}
          </p>
        </button>
        <div className="flex items-center gap-3">
          <StatusBadge status={statusToken(o.status)} />
          <p className="text-sm font-semibold text-[var(--color-text-primary)]">
            ₹ {o.total.toLocaleString('en-IN')}
          </p>
          <button
            type="button"
            onClick={() => setOpenId((p) => (p === o.id ? null : o.id))}
            className="inline-flex h-7 w-7 items-center justify-center rounded text-content-muted hover:bg-[var(--color-bg-card-hover)]"
            aria-label={openId === o.id ? 'Collapse timeline' : 'Show timeline'}
          >
            <ChevronRight
              className={`h-4 w-4 transition-transform ${openId === o.id ? 'rotate-90' : ''}`}
            />
          </button>
        </div>
      </div>
      {openId === o.id && (
        <div className="border-t border-[var(--color-border)] bg-[var(--color-bg-app)]/40 px-5 py-4">
          <div className="mb-4 flex flex-wrap gap-2">
            {isCancellable(o.status) && (
              <Button size="sm" variant="outline" onClick={() => setCancelling(o)}>
                <XCircle className="h-3.5 w-3.5" />
                Cancel order
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => doReorder(o)}>
              <RefreshCw className="h-3.5 w-3.5" />
              Reorder
            </Button>
            <Button size="sm" variant="outline" onClick={() => doDownloadInvoice(o)}>
              <Download className="h-3.5 w-3.5" />
              Download invoice
            </Button>
          </div>

          <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">
            Order timeline
          </p>
          <ol className="grid gap-3">
            {o.history.map((h, i) => {
              const Icon = STAGE_ICON[h.stage];
              const last = i === o.history.length - 1;
              return (
                <li key={`${h.stage}-${i}`} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <Icon
                      className={`h-4 w-4 ${
                        last ? 'text-[var(--color-primary)]' : 'text-[var(--color-text-muted)]'
                      }`}
                    />
                    {!last && <div className="mt-1 h-6 w-px bg-[var(--color-border)]" />}
                  </div>
                  <div className="grid">
                    <p
                      className={`text-sm ${
                        last
                          ? 'font-semibold text-[var(--color-text-primary)]'
                          : 'text-[var(--color-text-primary)]'
                      }`}
                    >
                      {STAGE_LABEL[h.stage]}
                    </p>
                    <p className="text-xs text-[var(--color-text-secondary)]">{h.at || '—'}</p>
                    {h.note && (
                      <p className="text-xs text-[var(--color-text-muted)]">{h.note}</p>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      )}
    </Card>
  );

  return (
    <div className="grid gap-3">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold text-[var(--color-text-primary)]">My orders</h1>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {!loading && sorted.length === 0 && !error && (
        <Card className="grid place-items-center gap-2 p-10 text-center">
          <Package className="h-8 w-8 text-[var(--color-text-muted)]" />
          <p className="text-sm font-medium text-[var(--color-text-primary)]">
            No orders yet
          </p>
          <p className="max-w-sm text-xs text-[var(--color-text-secondary)]">
            Orders you place will appear here with live tracking and quick actions.
          </p>
        </Card>
      )}

      {/*
       * SSO NEW ISSUE 3 — active section renders every order whose status
       * is NOT the code-owned Returned=6 value. When the whole list is
       * empty we skip the section entirely so the empty-state card above
       * (which already handles zero orders) doesn't get a redundant
       * "No active orders" sibling.
       */}
      {activeOrders.length > 0 && activeOrders.map(renderOrderCard)}

      {/*
       * SSO NEW ISSUE 3 — dedicated "Returns & Refunds" section for orders
       * the admin marked Returned via the panel's OrderReturn workflow.
       * Only rendered when there's at least one return so the section
       * heading doesn't create visual noise for the majority of customers.
       */}
      {returnedOrders.length > 0 && (
        <>
          <div className="mt-4 flex items-center justify-between">
            <h2 className="text-base font-semibold text-[var(--color-text-primary)]">
              Returns &amp; refunds
            </h2>
            <p className="text-xs text-[var(--color-text-secondary)]">
              {returnedOrders.length} {returnedOrders.length === 1 ? 'order' : 'orders'}
            </p>
          </div>
          {returnedOrders.map(renderOrderCard)}
        </>
      )}

      <ConfirmDialog
        isOpen={!!cancelling}
        title="Cancel this order?"
        message={`Order ${cancelling?.raw.orderNumber ?? ''} will be cancelled and refunded if already paid.`}
        confirmLabel={acting ? 'Cancelling…' : 'Cancel order'}
        cancelLabel="Keep order"
        onConfirm={doCancel}
        onClose={() => setCancelling(null)}
        variant="danger"
      />
    </div>
  );
}
